import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import userPreferencesService, {
  UserPreferences,
  UpdateUserPreferencesDto,
  ThemeSettings,
  NotificationPreferences,
  WorkflowDefaults,
  PrinterSettings,
} from '../services/userPreferencesService';

interface UserPreferencesState {
  // State
  preferences: UserPreferences | null;
  loading: boolean;
  error: string | null;
  /** Pharmacy id `preferences` currently reflects — lets fetchPreferences
   *  skip a redundant GET when asked for a pharmacy it already has loaded. */
  loadedPharmacyId: string | null;
  /** In-flight fetch (if any), and which pharmacy it's for — lets concurrent
   *  callers (MainLayout, useDefaultLandingPage both call this independently
   *  on a fresh browser, PRX-40) share one request instead of firing two. */
  fetchPromise: Promise<void> | null;
  fetchPharmacyId: string | null;

  // Actions
  fetchPreferences: (pharmacyId: string, options?: { force?: boolean }) => Promise<void>;
  updatePreferences: (pharmacyId: string, updates: UpdateUserPreferencesDto) => Promise<void>;
  updateThemeSettings: (pharmacyId: string, themeSettings: Partial<ThemeSettings>) => Promise<void>;
  updateNotificationPreferences: (
    pharmacyId: string,
    notificationPreferences: Partial<NotificationPreferences>,
  ) => Promise<void>;
  updateWorkflowDefaults: (
    pharmacyId: string,
    workflowDefaults: Partial<WorkflowDefaults>,
  ) => Promise<void>;
  updatePrinterSettings: (
    pharmacyId: string,
    printerSettings: Partial<PrinterSettings>,
  ) => Promise<void>;
  resetPreferences: (pharmacyId: string) => Promise<void>;
  clearError: () => void;
}

export const useUserPreferencesStore = create<UserPreferencesState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        preferences: null,
        loading: false,
        error: null,
        loadedPharmacyId: null,
        fetchPromise: null,
        fetchPharmacyId: null,

        // Fetch user preferences for a pharmacy. Deduped here (not by each
        // caller) since MainLayout and useDefaultLandingPage both call this
        // independently on a fresh browser (PRX-40 review): a concurrent
        // call for the same pharmacy joins the in-flight request instead of
        // firing a second GET, and a call once that pharmacy is already
        // loaded is a no-op — pass `{ force: true }` to bypass both (used by
        // resetPreferences, which needs a genuinely fresh fetch).
        fetchPreferences: async (pharmacyId: string, options?: { force?: boolean }) => {
          const force = options?.force ?? false;
          const state = get();

          if (!force) {
            if (state.fetchPromise && state.fetchPharmacyId === pharmacyId) {
              return state.fetchPromise;
            }
            if (state.preferences && state.loadedPharmacyId === pharmacyId) {
              return;
            }
          }

          set({ loading: true, error: null });

          const promise = (async () => {
            try {
              const preferences = await userPreferencesService.getUserPreferences(pharmacyId);
              set({
                preferences,
                loading: false,
                loadedPharmacyId: pharmacyId,
                fetchPromise: null,
                fetchPharmacyId: null,
              });
            } catch (error: any) {
              set({
                error: error.message || 'Failed to fetch preferences',
                loading: false,
                fetchPromise: null,
                fetchPharmacyId: null,
              });
            }
          })();

          set({ fetchPromise: promise, fetchPharmacyId: pharmacyId });
          return promise;
        },

        // Update user preferences
        updatePreferences: async (pharmacyId: string, updates: UpdateUserPreferencesDto) => {
          set({ loading: true, error: null });
          try {
            const preferences = await userPreferencesService.updateUserPreferences(
              pharmacyId,
              updates,
            );
            set({ preferences, loading: false });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to update preferences',
              loading: false,
            });
            throw error;
          }
        },

        // Update theme settings only
        updateThemeSettings: async (
          pharmacyId: string,
          themeSettings: Partial<ThemeSettings>,
        ) => {
          const { preferences } = get();
          if (!preferences) {
            throw new Error('No preferences loaded');
          }

          await get().updatePreferences(pharmacyId, {
            themeSettings: {
              ...preferences.themeSettings,
              ...themeSettings,
            },
          });
        },

        // Update notification preferences only
        updateNotificationPreferences: async (
          pharmacyId: string,
          notificationPreferences: Partial<NotificationPreferences>,
        ) => {
          const { preferences } = get();
          if (!preferences) {
            throw new Error('No preferences loaded');
          }

          await get().updatePreferences(pharmacyId, {
            notificationPreferences: {
              ...preferences.notificationPreferences,
              ...notificationPreferences,
            },
          });
        },

        // Update workflow defaults only
        updateWorkflowDefaults: async (
          pharmacyId: string,
          workflowDefaults: Partial<WorkflowDefaults>,
        ) => {
          const { preferences } = get();
          if (!preferences) {
            throw new Error('No preferences loaded');
          }

          await get().updatePreferences(pharmacyId, {
            workflowDefaults: {
              ...preferences.workflowDefaults,
              ...workflowDefaults,
            },
          });
        },

        // Update printer settings only
        updatePrinterSettings: async (
          pharmacyId: string,
          printerSettings: Partial<PrinterSettings>,
        ) => {
          const { preferences } = get();
          if (!preferences) {
            throw new Error('No preferences loaded');
          }

          await get().updatePreferences(pharmacyId, {
            printerSettings: {
              ...preferences.printerSettings,
              ...printerSettings,
            },
          });
        },

        // Reset preferences to defaults
        resetPreferences: async (pharmacyId: string) => {
          set({ loading: true, error: null });
          try {
            await userPreferencesService.deleteUserPreferences(pharmacyId);
            // Fetch new default preferences — force, since the dedup cache
            // above still thinks the (now-deleted) preferences for this
            // pharmacy are current and would otherwise skip this fetch.
            await get().fetchPreferences(pharmacyId, { force: true });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to reset preferences',
              loading: false,
            });
            throw error;
          }
        },

        // Clear error
        clearError: () => set({ error: null }),
      }),
      {
        name: 'user-preferences-store',
        // Only persist the preferences, not loading/error states
        partialize: (state) => ({ preferences: state.preferences }),
      },
    ),
    { name: 'UserPreferencesStore' },
  ),
);
